/* eslint-disable no-undef */
/**
 * Service Agreement (E-Series Robot Service Agreement) layout configuration.
 *
 * This file is the SINGLE SOURCE OF TRUTH for:
 *   1. The static template PDF (`public/agreementLocation.pdf`) which is regenerated
 *      by `scripts/generateServiceAgreement.mjs` using these exact coordinates.
 *   2. Form-fill positions used in `Contract.tsx` and `OrderDetail.tsx` to draw
 *      values on top of that template.
 *
 * If you change a Y/X here, run `node scripts/generateServiceAgreement.mjs`
 * to regenerate the template PDF so it matches.
 *
 * Authored as a native ES module (`.mjs`) so it works identically in the
 * webpack/React bundle (production build was rejecting CommonJS `module.exports`
 * here) and in the Node build scripts, which import it via ESM.
 */

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const MARGIN_X = 36;
const COL_DIVIDER_X = 306;
const VALUE_COL_X = 312; // 6pt padding after divider
const ROW_HEIGHT = 22;
const VALUE_FONT_SIZE = 10;

/** Helper: y-baseline for the value text in a row whose top is at `rowTop`. */
function valueBaseline(rowTop) {
  return rowTop - 14;
}

const TABLE0_TOP = 596; // Client info table starts here on page 0
const TABLE1_TOP = 346; // Cost / term info table starts here on page 0

/** Page 0 (form) – effective date row. */
const EFFECTIVE_DATE_Y = 608;

/** Page 0 – Client info table rows (top y of each row). */
const TABLE0_ROWS = [
  { key: 'businessName',             label: 'Business Name / Location' },
  { key: 'serviceAddress',           label: 'Service Address' },
  { key: 'cityStateZip',             label: 'City, State, Zip' },
  { key: 'locationContactNamePhone', label: 'Location Contact Name and Phone' },
  { key: 'locationContactEmail',     label: 'Location Contact Email' },
  { key: 'billingEntity',            label: 'Billing Entity' },
  { key: 'billingContactNamePhone',  label: 'Billing Contact Name and Phone' },
  { key: 'billingContactEmail',      label: 'Billing Contact Email' },
  { key: 'authorizedPersonName',     label: 'Authorized Person Name' },
  { key: 'authorizedPersonEmail',    label: 'Authorized Person Email' },
  { key: 'authorizedPersonPhone',    label: 'Authorized Person Phone' },
];

/** Page 0 – Cost / term info table rows. The right cell of cost rows shows "$" prefix. */
const TABLE1_ROWS = [
  { key: 'agreementTermLength',       label: 'Agreement Term Length',       prefix: '' },
  { key: 'termStartDate',             label: 'Term Start Date',             prefix: '' },
  { key: 'implementationCost',        label: 'Implementation Cost',         prefix: '$' },
  { key: 'shippingFee',               label: 'Shipping Fee',                prefix: '$' },
  { key: 'monthlyRoboticServiceCost', label: 'Monthly Robotic Service Cost', prefix: '$' },
  { key: 'additionalAccessoriesCost', label: 'Additional Accessories Cost', prefix: '$' },
  { key: 'implementationCostDue',     label: 'Implementation Cost Due',     prefix: '$' },
  { key: 'totalMonthlyCost',          label: 'Total Monthly Cost',          prefix: '$' },
];

/** Page 1 – Robot quantity list. Each line top y is anchored from a header. */
const QUANTITY_LIST_TOP = 740; // y of the section header
const QUANTITY_LINE_GAP = 18;
const QUANTITY_FIRST_LINE_Y = 712; // baseline of the first qty value (Qty <here>: TIM-E Bot ...)
const QUANTITY_VALUE_X = 64;       // x where the qty number is drawn (after "Qty ")

/**
 * Page 1 – the "Robot Quantity, Model and Monthly Cost per Item" catalog.
 *
 * `price` is the DEFAULT monthly price per unit. A contract can override any of
 * them for that deal alone (`form_data.unitPrices`), which is why the price is
 * data here instead of text baked into the template: the template prints only
 * "Qty ____" and the fill code draws ": <label> <suffix>" from whichever price
 * applies. `unit` is the wording after the amount and `zeroNote` replaces the
 * whole suffix while the price is 0.
 */
const QUANTITY_ROWS = [
  { key: 'qtyTimEBot',                       label: 'TIM-E Bot',                    price: 3000, unit: 'each' },
  { key: 'qtyTimECharger',                   label: 'TIM-E Charger',                price: 0,    unit: 'each', zeroNote: 'included with Bot' },
  { key: 'qtyBaseMetalMonthly',              label: 'Base - Metal',                 price: 15,   unit: 'per base' },
  { key: 'qtyInsulatedFoodTransportMonthly', label: 'Insulated Food Transport',     price: 47,   unit: 'Non-powered', decimals: 2 },
  { key: 'qtyWheeledBinMonthly',             label: 'Wheeled Bin',                  price: 35,   unit: 'per bin' },
  { key: 'qtyUniversalPlatformMonthly',      label: 'Universal Platform',           price: 35,   unit: 'per platform' },
  { key: 'qtyDoorOpenersMonthly',            label: 'Door Openers',                 price: 30,   unit: 'per door' },
  { key: 'qtyNeuralTechBrainMonthly',        label: 'NeuralTech Brain',             price: 25,   unit: 'per unit' },
  { key: 'qtyElevatorHardwareMonthly',       label: 'Elevator Hardware + Software', price: 45,   unit: 'per elevator' },
  { key: 'qtyLuggageCartMonthly',            label: 'Luggage Cart',                 price: 35,   unit: 'per cart' },
  { key: 'qtyKebbi',                         label: 'Kebb-i',                       price: 300,  unit: 'each' },
];

/**
 * The "Additional Accessories" catalog — one-time charges, so these feed
 * Additional Accessories Cost rather than the monthly total. Same shape as
 * QUANTITY_ROWS, but with no reserved lines on the template: the fill code
 * prints the ones given a quantity into the open space below the monthly list.
 */
const ACCESSORY_ROWS = [
  { key: 'qtyBaseMetalOneTime',              label: 'Base - Metal',                 price: 400, unit: 'each' },
  { key: 'qtyInsulatedFoodTransportOneTime', label: 'Insulated Food Transport',     price: 450, unit: 'each' },
  { key: 'qtyWheeledBinOneTime',             label: 'Wheeled Bin',                  price: 475, unit: 'each' },
  { key: 'qtyUniversalPlatformOneTime',      label: 'Universal Platform',           price: 285, unit: 'each' },
  { key: 'qtyPlasticBags',                   label: 'Plastic Bags',                 price: 100, unit: 'per 1,000' },
  { key: 'qtyDoorOpenerHardwareOneTime',     label: 'Door Opener Hardware',         price: 650, unit: 'each' },
  { key: 'qtyHandheldTablet',                label: 'Handheld Tablet w/ App',       price: 350, unit: 'each' },
];

/** "3000" -> "3,000"; `decimals` pins rows whose printed style has cents. */
function formatCatalogPrice(price, decimals) {
  const amount = Number(price);
  if (!Number.isFinite(amount)) return '0';
  const places = decimals != null ? decimals : Number.isInteger(amount) ? 0 : 2;
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  });
}

/**
 * The parenthesised price that follows a catalog label, e.g. "($15 per base)".
 * Pass `price` to render an override; omit it for the catalog default.
 */
function catalogRowSuffix(row, price) {
  const effective = price == null ? row.price : Number(price);
  if (!(effective > 0) && row.zeroNote) return `(${row.zeroNote})`;
  return `($${formatCatalogPrice(effective, row.decimals)}${row.unit ? ` ${row.unit}` : ''})`;
}

/**
 * Page 1 – Initials blank after the third-party electrician paragraph, as
 * placed while nothing was added to the monthly list. The fill code draws the
 * blank after "Check initial to acknowledge", one QUANTITY_LINE_GAP lower per
 * added list line; `clientInitialsPlacement` in contractExtrasPdf.ts gives the
 * spot on a filled PDF.
 */
const INITIALS_PLACEMENT = { x: 200, y: 372, fontSize: 10, pageIndex: 1 };

/**
 * Last page – authorization signature table.
 * Left cell: TechForce signature (Philip D Garcia)
 * Right cell: Client signature
 *
 * The signature row in the table is intentionally TALLER than the surrounding
 * rows (h=60) to fit a 180x44 signature image cleanly above the text baseline.
 */
const SIGNATURE_PAGE_INDEX = 4; // 5th page (0-indexed) – set by generator
const AUTHORIZATION_HEADING_Y = 640;
const SIGNATURE_TABLE_TOP_Y = 620;
const SIGNATURE_ROW_TALL_HEIGHT = 60;
const SIGNATURE_ROW_NORMAL_HEIGHT = 20;
const SIGNATURE_DIVIDER_X = 306;

// Y coordinate of the "Signature:" label baseline (computed): row 2 starts at
// 620 - 2*20 = 580 and is 60pt tall, with the text drawn near its top.
const SIGNATURE_ROW_TOP_Y = SIGNATURE_TABLE_TOP_Y - 2 * SIGNATURE_ROW_NORMAL_HEIGHT;
// Bottom of the signature row (= top of "Name" row)
const SIGNATURE_ROW_BOTTOM_Y = SIGNATURE_ROW_TOP_Y - SIGNATURE_ROW_TALL_HEIGHT;

// Signature image: sits inside the tall row, just above its bottom border.
const TECHFORCE_SIGNATURE_PLACEMENT = {
  x: 110,                                  // Left cell, just right of "Signature:" label
  y: SIGNATURE_ROW_BOTTOM_Y + 4,           // Bottom of image, 4pt above row's bottom border
  width: 180,
  height: 44,
  pageIndex: SIGNATURE_PAGE_INDEX,
};
const CLIENT_SIGNATURE_PLACEMENT = {
  x: SIGNATURE_DIVIDER_X + 74,             // Right cell, just right of "Signature:" label
  y: SIGNATURE_ROW_BOTTOM_Y + 4,
  width: 180,
  height: 44,
  pageIndex: SIGNATURE_PAGE_INDEX,
};

// Client (right-column) "Name:" and "Position:" rows of the Authorization
// table. These are filled with the authorized person's name and title so the
// signer's identity appears at the end of the PDF.
// Row tops in the signature table: header=620, entity=600, signature=580,
// name=520 (after the 60pt signature row), position=500. Baseline = top - 14.
const CLIENT_NAME_PLACEMENT = {
  x: SIGNATURE_DIVIDER_X + 44,             // After the printed "Name:" label
  y: SIGNATURE_ROW_BOTTOM_Y - SIGNATURE_ROW_NORMAL_HEIGHT + 6, // 520 - 14 = 506
  fontSize: VALUE_FONT_SIZE,
  pageIndex: SIGNATURE_PAGE_INDEX,
};
const CLIENT_POSITION_PLACEMENT = {
  x: SIGNATURE_DIVIDER_X + 54,             // After the printed "Position:" label
  y: SIGNATURE_ROW_BOTTOM_Y - 2 * SIGNATURE_ROW_NORMAL_HEIGHT + 6, // 500 - 14 = 486
  fontSize: VALUE_FONT_SIZE,
  pageIndex: SIGNATURE_PAGE_INDEX,
};

/** Build the full TEXT_PLACEMENTS map used by Contract.tsx / OrderDetail.tsx. */
function buildTextPlacements() {
  const placements = {};

  // Page 0 – effective date (drawn next to the bold "Effective Date:" label)
  placements.effectiveDate = { x: 120, y: EFFECTIVE_DATE_Y, fontSize: VALUE_FONT_SIZE, pageIndex: 0 };

  // Page 0 – Client info table
  TABLE0_ROWS.forEach((row, idx) => {
    const rowTop = TABLE0_TOP - idx * ROW_HEIGHT;
    placements[row.key] = {
      x: VALUE_COL_X,
      y: valueBaseline(rowTop),
      fontSize: VALUE_FONT_SIZE,
      pageIndex: 0,
    };
  });

  // Page 0 – Cost / term info table. For $-prefixed rows, the value goes after the printed "$".
  TABLE1_ROWS.forEach((row, idx) => {
    const rowTop = TABLE1_TOP - idx * ROW_HEIGHT;
    const x = row.prefix === '$' ? VALUE_COL_X + 12 : VALUE_COL_X;
    placements[row.key] = {
      x,
      y: valueBaseline(rowTop),
      fontSize: VALUE_FONT_SIZE,
      pageIndex: 0,
    };
  });

  // Implementation cost shows up twice in the layout? No — only the table 1 row.
  // (Old PDF had `implementationCostPage2`; new PDF doesn't, so we omit it.)

  // Page 1 – Quantity list: value drawn at fixed X for each item.
  QUANTITY_ROWS.forEach((row, idx) => {
    placements[row.key] = {
      x: QUANTITY_VALUE_X,
      y: QUANTITY_FIRST_LINE_Y - idx * QUANTITY_LINE_GAP,
      fontSize: VALUE_FONT_SIZE,
      pageIndex: 1,
    };
  });

  // Initials (third-party electrician acknowledgment) – inside body paragraph on page 1.
  placements.clientInitials = { ...INITIALS_PLACEMENT };

  // Last page – signatures
  placements.techforceSignature = { ...TECHFORCE_SIGNATURE_PLACEMENT };
  placements.clientSignatureLastPage = { ...CLIENT_SIGNATURE_PLACEMENT };

  // Last page – authorized person identity in the client signature block.
  placements.clientName = { ...CLIENT_NAME_PLACEMENT };
  placements.clientPosition = { ...CLIENT_POSITION_PLACEMENT };

  return placements;
}

const TEXT_PLACEMENTS = buildTextPlacements();

export {
  PAGE_WIDTH,
  PAGE_HEIGHT,
  MARGIN_X,
  COL_DIVIDER_X,
  VALUE_COL_X,
  ROW_HEIGHT,
  VALUE_FONT_SIZE,
  EFFECTIVE_DATE_Y,
  TABLE0_TOP,
  TABLE1_TOP,
  TABLE0_ROWS,
  TABLE1_ROWS,
  QUANTITY_LIST_TOP,
  QUANTITY_LINE_GAP,
  QUANTITY_FIRST_LINE_Y,
  QUANTITY_VALUE_X,
  QUANTITY_ROWS,
  ACCESSORY_ROWS,
  catalogRowSuffix,
  INITIALS_PLACEMENT,
  SIGNATURE_PAGE_INDEX,
  AUTHORIZATION_HEADING_Y,
  SIGNATURE_TABLE_TOP_Y,
  SIGNATURE_ROW_TOP_Y,
  SIGNATURE_ROW_BOTTOM_Y,
  SIGNATURE_ROW_TALL_HEIGHT,
  SIGNATURE_ROW_NORMAL_HEIGHT,
  SIGNATURE_DIVIDER_X,
  CLIENT_NAME_PLACEMENT,
  CLIENT_POSITION_PLACEMENT,
  TECHFORCE_SIGNATURE_PLACEMENT,
  CLIENT_SIGNATURE_PLACEMENT,
  TEXT_PLACEMENTS,
};
